import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
  PieChart, Pie, Cell, BarChart, Bar,
} from "recharts";
import { Loader2, TrendingUp, Users, Target, Euro, ArrowLeft, LayoutList, Clock, Copy, RotateCw, AlertCircle, PhoneCall, LogOut, Images } from "lucide-react";
import { Logo } from "../components/site/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLORS = ["#163A5F", "#F28C28", "#4A90D9", "#7BC67E", "#9B8AFB", "#F2C94C"];

function Card({ icon: Icon, label, value, sub, accent }) {
  return (
    <div className="rounded-2xl bg-white border border-gray-100 p-5">
      <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${accent ? "bg-brand-orange/15 text-brand-orange" : "bg-navy/10 text-navy"}`}>
        <Icon size={20} />
      </div>
      <p className="mt-3 text-3xl font-display font-extrabold text-navy">{value}</p>
      <p className="text-sm text-ink/55">{label}</p>
      {sub && <p className="text-xs text-ink/40 mt-1">{sub}</p>}
    </div>
  );
}

export default function Dashboard() {
  const { logout } = useAuth();
  const [data, setData] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/dashboard`).then((r) => setData(r.data)),
      axios.get(`${API}/reports/monthly`).then((r) => setReport(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-surface"><Loader2 className="animate-spin text-navy" /></div>;

  const priorityData = data ? Object.entries(data.by_priority).map(([name, value]) => ({ name, value })) : [];
  const MONTHS_IT = ["gen", "feb", "mar", "apr", "mag", "giu", "lug", "ago", "set", "ott", "nov", "dic"];
  const fmtMonth = (m) => { if (!m) return ""; const [y, mo] = m.split("-"); return `${MONTHS_IT[parseInt(mo, 10) - 1]} ${y}`; };
  const eur = (n) => `€ ${(n || 0).toLocaleString("it-IT", { maximumFractionDigits: 0 })}`;
  const reportSeries = report ? report.months.map((m) => ({ ...m, label: fmtMonth(m.month) })) : [];

  return (
    <div className="min-h-screen bg-surface">
      <header className="sticky top-0 z-10 bg-white/90 backdrop-blur-xl border-b border-gray-100">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link to="/"><Logo /></Link>
          <div className="flex items-center gap-3">
            <Link to="/crm" data-testid="dash-to-crm" className="inline-flex items-center gap-1.5 rounded-full bg-navy px-4 py-2 text-sm font-semibold text-white hover:bg-navy-deep transition-colors">
              <LayoutList size={15} /> Richieste
            </Link>
            <Link to="/crm/galleria" data-testid="dash-to-gallery" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-4 py-2 text-sm font-semibold text-ink/70 hover:text-navy hover:border-navy/30 transition-colors">
              <Images size={15} /> <span className="hidden sm:inline">Galleria</span>
            </Link>
            <Link to="/" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-semibold text-ink/60 hover:text-navy">
              <ArrowLeft size={16} /> Sito
            </Link>
            <button onClick={logout} data-testid="dash-logout" className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 px-3.5 py-2 text-sm font-semibold text-ink/70 hover:text-red-600 hover:border-red-200 transition-colors">
              <LogOut size={15} /> <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 py-8">
        <h1 className="font-display font-extrabold text-3xl text-navy">Dashboard</h1>
        <p className="text-ink/55 mt-1">Panoramica lead e conversioni.</p>

        <div className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Card icon={Users} label="Lead oggi" value={data.today} />
          <Card icon={TrendingUp} label="Questa settimana" value={data.week} />
          <Card icon={LayoutList} label="Questo mese" value={data.month} />
          <Card icon={Users} label="Totale lead" value={data.total} />
          <Card icon={Target} label="Conversioni" value={data.conversions} sub={`Tasso ${data.conversion_rate}%`} accent />
          <Card icon={Euro} label="Valore stimato" value={`€ ${data.valore_pipeline.toLocaleString("it-IT")}`} accent />
        </div>

        {data.ops && (
          <>
            <h2 className="mt-8 font-display font-bold text-lg text-navy">Operatività (velocità di risposta)</h2>
            <div className="mt-3 grid grid-cols-2 lg:grid-cols-5 gap-3">
              <Card icon={Clock} label="Tempo medio 1ª risposta" value={data.ops.avg_first_response_minutes > 0 ? `${data.ops.avg_first_response_minutes} min` : "—"} accent />
              <Card icon={PhoneCall} label="Lead contattati" value={data.ops.leads_contacted} />
              <Card icon={AlertCircle} label="Senza azione" value={data.ops.leads_no_action} />
              <Card icon={Copy} label="Bozze copiate" value={data.ops.drafts_copied} />
              <Card icon={RotateCw} label="Bozze rigenerate" value={data.ops.drafts_regenerated} />
            </div>
          </>
        )}

        {/* Report mensile fatturato (lavori conclusi, valore lavoro) */}
        {report && (
          <div data-testid="dash-monthly-report" className="mt-8">
            <h2 className="font-display font-bold text-lg text-navy">Report mensile fatturato</h2>
            <p className="text-ink/55 text-sm mt-1">Lavori conclusi per mese · basato sul “valore lavoro”.</p>
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="rounded-2xl bg-white border border-gray-100 p-5" data-testid="report-current-revenue">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-brand-orange/15 text-brand-orange"><Euro size={20} /></div>
                <p className="mt-3 text-3xl font-display font-extrabold text-navy">{eur(report.current.revenue)}</p>
                <p className="text-sm text-ink/55">Fatturato {fmtMonth(report.current.month)}</p>
                <p className="text-xs mt-1 font-semibold" data-testid="report-delta">
                  {report.delta_pct === null ? (
                    <span className="text-ink/40">nessun dato mese precedente</span>
                  ) : (
                    <span className={report.delta >= 0 ? "text-emerald-600" : "text-red-600"}>
                      {report.delta >= 0 ? "▲" : "▼"} {Math.abs(report.delta_pct)}% vs {fmtMonth(report.previous.month)} ({eur(report.previous.revenue)})
                    </span>
                  )}
                </p>
              </div>
              <div className="rounded-2xl bg-white border border-gray-100 p-5" data-testid="report-current-count">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-navy/10 text-navy"><Target size={20} /></div>
                <p className="mt-3 text-3xl font-display font-extrabold text-navy">{report.current.count}</p>
                <p className="text-sm text-ink/55">Lavori conclusi ({fmtMonth(report.current.month)})</p>
              </div>
              <div className="rounded-2xl bg-white border border-gray-100 p-5" data-testid="report-current-average">
                <div className="h-10 w-10 rounded-xl flex items-center justify-center bg-navy/10 text-navy"><TrendingUp size={20} /></div>
                <p className="mt-3 text-3xl font-display font-extrabold text-navy">{eur(report.current.average)}</p>
                <p className="text-sm text-ink/55">Valore medio per lavoro</p>
              </div>
            </div>
            <div className="mt-4 rounded-2xl bg-white border border-gray-100 p-5">
              <h3 className="font-display font-bold text-navy mb-4">Fatturato per mese</h3>
              {reportSeries.length === 0 ? (
                <p className="text-sm text-ink/50 py-8 text-center">Nessun lavoro concluso registrato.</p>
              ) : (
                <ResponsiveContainer width="100%" height={260}>
                  <BarChart data={reportSeries} margin={{ left: -20 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                    <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#888" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#888" }} />
                    <Tooltip formatter={(v, n) => n === "revenue" ? [eur(v), "Fatturato"] : [v, n]} />
                    <Bar dataKey="revenue" fill="#F28C28" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </div>
        )}

        <div className="mt-6 grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 rounded-2xl bg-white border border-gray-100 p-5">
            <h3 className="font-display font-bold text-navy mb-4">Lead ultimi 14 giorni</h3>
            <ResponsiveContainer width="100%" height={260}>
              <AreaChart data={data.timeseries} margin={{ left: -20 }}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F28C28" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#F28C28" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="date" tickFormatter={(d) => d.slice(5)} tick={{ fontSize: 11, fill: "#888" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#888" }} />
                <Tooltip />
                <Area type="monotone" dataKey="leads" stroke="#F28C28" strokeWidth={2.5} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl bg-white border border-gray-100 p-5">
            <h3 className="font-display font-bold text-navy mb-4">Origine traffico</h3>
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.by_source} dataKey="value" nameKey="name" innerRadius={50} outerRadius={90} paddingAngle={2}>
                  {data.by_source.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-2 flex flex-wrap gap-2 justify-center">
              {data.by_source.map((s, i) => (
                <span key={s.name} className="inline-flex items-center gap-1.5 text-xs text-ink/60">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ background: COLORS[i % COLORS.length] }} /> {s.name} ({s.value})
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-4 grid lg:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-white border border-gray-100 p-5">
            <h3 className="font-display font-bold text-navy mb-4">Lead per stato</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.by_state} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 9, fill: "#888" }} interval={0} angle={-15} textAnchor="end" height={60} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#888" }} />
                <Tooltip />
                <Bar dataKey="value" fill="#163A5F" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          <div className="rounded-2xl bg-white border border-gray-100 p-5">
            <h3 className="font-display font-bold text-navy mb-4">Priorità lead</h3>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={priorityData} margin={{ left: -20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" vertical={false} />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#888" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#888" }} />
                <Tooltip />
                <Bar dataKey="value" radius={[6, 6, 0, 0]}>
                  {priorityData.map((p, i) => (
                    <Cell key={i} fill={p.name === "Alta" ? "#F28C28" : p.name === "Media" ? "#4A90D9" : "#9AA5B1"} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
